import React, { useMemo } from 'react';
import { Center, Flex, Group, Spoiler, Stack, Text, Tooltip } from '@mantine/core';
import RunsStats from '@/pages/AutomationPage/components/RunsStats.tsx';
import { EditableText } from '@/components/EditableText.tsx';
import useUpdateAutomation from '@/hooks/api/automations/useUpdateAutomation.ts';
import AppIcon from '@/components/AppIcon.tsx';
import { HiOutlineRectangleGroup } from 'react-icons/hi2';
import useRetrieveStaticDatasets from '@/hooks/api/datasets/static/useRetrieveStaticDatasets.ts';
import KnowledgeBaseBadge from '@/pages/AutomationPage/components/KnowledgeBaseBadge.tsx';
import useRetrieveIntegrations from '@/hooks/api/integrations/useRetrieveIntegrations.ts';
import IntegrationLogo from '@/pages/Integrations/components/IntegrationLogo.tsx';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { collectUsedIntegrations } from '@/utils/stepUtils.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import { Automation } from '@/types/automations.ts';
import {
  AUTOMATION_DESCRIPTION_MAX_LENGTH,
  AUTOMATION_NAME_MAX_LENGTH,
} from '@/utils/constants.ts';

type props = {
  automation: Automation;
  filterStatuses: ExecutionStatus[];
  toggleStatus: (status: ExecutionStatus) => void;
  onlyStatus: (status: ExecutionStatus) => void;
  resetStatuses: () => void;
};

export function AutomationPageHeader({
  automation,
  filterStatuses,
  toggleStatus,
  onlyStatus,
  resetStatuses,
}: props) {
  const { mutate: updateName, isPending: isUpdatingName } = useUpdateAutomation();
  const { mutate: updateDescription, isPending: isUpdatingDescription } = useUpdateAutomation();

  const { data: staticDatasets } = useRetrieveStaticDatasets();

  const { data: integrations } = useRetrieveIntegrations();

  const { data: latestVersion } = useRetrieveLatestAutomationVersion();

  const integrationsUsed = useMemo(
    () => collectUsedIntegrations(latestVersion?.steps, integrations?.results),
    [integrations?.results, latestVersion?.steps],
  );

  const uniqueIntegrations = [...new Map(integrationsUsed.map((p) => [p.name, p])).values()];

  return (
    <>
      <Group
        p={'xl'}
        wrap={'nowrap'}
        bg={'#FAFAFA'}
        style={{ borderRadius: '12px' }}
        justify={'space-between'}
        w="100%"
        gap={'lg'}
      >
        <Stack gap={0} w={'100%'} maw={'600px'} style={{ flex: 1, minWidth: 0 }}>
          <EditableText
            name={automation.name}
            onSave={(name) => updateName({ id: automation.id, name })}
            saving={isUpdatingName}
            size={'lg'}
            placeholder={'Name'}
            leftSection={<AppIcon Icon={HiOutlineRectangleGroup} color={'#9E9E9E'} size={24} />}
            leftSectionWidth={'40px'}
            fw={500}
            maxLength={AUTOMATION_NAME_MAX_LENGTH}
          />
          <Flex ml={'-4px'} w={'fill-available'}>
            <EditableText
              name={automation.description}
              onSave={(description) => updateDescription({ id: automation.id, description })}
              saving={isUpdatingDescription}
              size={'sm'}
              inputType={'textarea'}
              lineClamp={3}
              placeholder={'Add a description'}
              maxLength={AUTOMATION_DESCRIPTION_MAX_LENGTH}
            />
          </Flex>

          <Group wrap={'nowrap'} mt={'md'} align={'start'} ml={'xs'}>
            <Text size={'12px'} c={'#757575'} style={{ textWrap: 'nowrap' }} mt={'6px'} w={'95px'}>
              Integrations
            </Text>

            <Group gap={'6px'} w={'100%'} style={{ flex: 1, minWidth: 0 }}>
              {uniqueIntegrations.map((provider) => (
                <Tooltip label={provider.display_name} openDelay={250} key={provider.name}>
                  <Center
                    w={'22px'}
                    h={'22px'}
                    bg={'white'}
                    style={{
                      borderRadius: '4px',
                      border: '0.5px solid #ECECED',
                      boxShadow:
                        '0 1px 1px 0 rgba(0, 0, 0, 0.04), 0 3px 6px -2px rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    <IntegrationLogo name={provider.name} size={'12px'} useBorderRadius={false} />
                  </Center>
                </Tooltip>
              ))}
            </Group>
          </Group>

          <Group wrap={'nowrap'} mt={'md'} align={'start'} ml={'xs'}>
            <Text size={'12px'} c={'#757575'} style={{ textWrap: 'nowrap' }} mt={'6px'} w={'95px'}>
              Knowledge Base
            </Text>

            <Spoiler
              maxHeight={82}
              showLabel="View all"
              hideLabel="Collapse"
              styles={{
                control: { color: '#868e96', fontSize: '12px', marginTop: '2px' },
              }}
              style={{ minWidth: 0, flex: 1 }}
            >
              <Group gap={'6px'} w={'100%'} style={{ flex: 1, minWidth: 0 }}>
                {staticDatasets?.results.map((s) => (
                  <KnowledgeBaseBadge key={s.id} dataset={s.dataset} />
                ))}
              </Group>
            </Spoiler>
          </Group>
        </Stack>
        <Stack
          gap={0}
          miw={{ base: '250px', md: '400px' }}
          w={{ base: '250px', md: '400px' }}
          display={{ base: 'none', sm: 'flex' }}
        >
          <RunsStats
            filterStatuses={filterStatuses}
            toggleStatus={toggleStatus}
            onlyStatus={onlyStatus}
            resetStatuses={resetStatuses}
          />
        </Stack>
      </Group>
    </>
  );
}

export default AutomationPageHeader;
